import { IconChevronDown } from '@bitrise/bitkit-v2';
import { Card, CardRootProps } from '@chakra-ui/react/card';
import { Collapsible } from '@chakra-ui/react/collapsible';
import { forwardRef, ReactNode } from 'react';

type StepLogCardProps = Omit<CardRootProps, 'children' | 'onChange'> & {
  children: ReactNode;
  expanded: boolean;
  header: ReactNode;
  onExpandedChange: (expanded: boolean) => void;
};

const StepLogCard = forwardRef<HTMLDivElement, StepLogCardProps>(
  ({ children, expanded, header, onExpandedChange, ...rest }, ref) => (
    <Card.Root ref={ref} {...rest}>
      <Collapsible.Root unstyled open={expanded} onOpenChange={({ open }) => onExpandedChange(open)}>
        <Collapsible.Trigger
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="full"
          paddingBlock="6"
          paddingInline="12"
          borderRadius="8"
          color="text/primary"
          cursor="pointer"
          _open={{
            backgroundColor: 'background/secondary',
            borderBottom: '1px solid',
            borderBottomColor: 'border/regular',
            borderBottomRadius: '0',
          }}
          _hover={{ backgroundColor: 'background/hover' }}
          _active={{ backgroundColor: 'background/active' }}
        >
          {header}
          <Collapsible.Indicator
            display="flex"
            transitionProperty="transform"
            transitionDuration="moderate"
            _open={{ transform: 'rotate(180deg)' }}
          >
            <IconChevronDown />
          </Collapsible.Indicator>
        </Collapsible.Trigger>
        <Collapsible.Content
          // Not `hidden`: a scroll container here would pin the log's sticky toolbar to the card instead of the page.
          overflow="clip"
          _open={{ animationName: 'expand-height, fade-in', animationDuration: 'moderate' }}
          _closed={{ animationName: 'collapse-height, fade-out', animationDuration: 'moderate' }}
        >
          {children}
        </Collapsible.Content>
      </Collapsible.Root>
    </Card.Root>
  ),
);

StepLogCard.displayName = 'StepLogCard';

export default StepLogCard;
